import { computed } from 'vue';
import { useMediaSelectionStore } from '@/pinia/stores/media/mediaSelectionStore';
import { useMediaStore } from '@/pinia/stores/media/mediaStore';
import { useKeyboardModifiers } from '@base-shared/composables/use-keyboard-modifiers';
import type { components } from '@base-shared/types/media.openapi';

type MediaItem = components['schemas']['MediaItem'];

/**
 * Composable for managing media item selection.
 *
 * Supports three selection modes driven by modifier keys:
 * - No modifier  → single select (replaces existing selection, or deselects if only item)
 * - Ctrl/Cmd     → multi-select toggle (add or remove individual items)
 * - Shift        → range select (replaces selection with a contiguous range from the
 *                  first selected item as anchor to the clicked item)
 *
 * Keyboard modifier state (Ctrl/Cmd, Shift) is tracked internally via
 * window keydown/keyup listeners, registered on mount and cleaned up on unmount.
 * @see useKeyboardModifiers
 */
export function useMediaSelection () {
  // Pinia stores
  const mediaSelectionStore = useMediaSelectionStore();
  const mediaStore = useMediaStore();

  // Composables
  const { isCtrlPressed, isShiftPressed } = useKeyboardModifiers();

  const selection = computed(() => mediaSelectionStore.selection);
  const hasSelection = computed(() => mediaSelectionStore.hasSelection);
  const selectedIds = computed(() => new Set(mediaSelectionStore.selection.map(item => item.resourceId)));
  const anchorItem = computed(() => mediaSelectionStore.anchorItem);
  const isFetchingDetails = computed(() => mediaSelectionStore.isFetchingDetails);

  /**
   * Selects a media item based on the currently held modifier keys.
   * @param {MediaItem} mediaItem - The item that was clicked
   * @param {MediaItem[]} currentPageItems - The items currently rendered on the page,
   *   required for shift-range resolution. Pass `mediaListPage.items.value` from the caller.
   */
  async function selectMedia (mediaItem: MediaItem, currentPageItems: MediaItem[] = []) {
    const currentSelection = mediaSelectionStore.selection;

    if (isCtrlPressed.value) {
      mediaSelectionStore.setSelection(multiSelectWithCtrl(currentSelection, mediaItem));
      await fetchAndPopulateDetails([mediaItem]);
      return;
    }

    if (isShiftPressed.value) {
      // Set selection data from the shift-click logic
      const selectedItems = multiSelectWithShift(currentSelection, mediaItem, currentPageItems);
      mediaSelectionStore.setSelection(selectedItems);

      // Fetch details for any newly selected items that aren't already in the selection store. This is to minimize redundant API calls when shift-selecting a large range that includes items already selected.
      const alreadyFetched = new Set(currentSelection.map(item => item.resourceId));
      const newItems = selectedItems.filter(item => !alreadyFetched.has(item.resourceId));
      if (newItems.length) await fetchAndPopulateDetails(newItems);
      return;
    }

    // No modifier — deselect if the clicked item is the only selected item (toggle off).
    const isMediaItemInSelection = currentSelection.some(selected => selected.resourceId === mediaItem.resourceId);
    if (currentSelection.length === 1 && isMediaItemInSelection) {
      // Clears selection state and the anchorItem
      mediaSelectionStore.clearSelection();
      return;
    }

    // Single select — replace whatever is currently selected with just this item.
    // Also update the anchor so subsequent shift-clicks range from here.
    mediaSelectionStore.setAnchorItem(mediaItem);
    mediaSelectionStore.setSelection([mediaItem]);
    await fetchAndPopulateDetails([mediaItem]);
  }

  /**
   * Toggles a single item in or out of the current selection.
   * Called when Ctrl/Cmd is held during a click.
   * @param {MediaItem[]} currentSelection
   * @param {MediaItem} mediaItem
   * @returns {MediaItem[]} Updated selection array
   */
  function multiSelectWithCtrl (currentSelection: MediaItem[], mediaItem: MediaItem): MediaItem[] {
    const isMediaItemInSelection = currentSelection.some(selected => selected.resourceId === mediaItem.resourceId);

    if (!isMediaItemInSelection) {
      // Item is not yet selected — add it.
      return [...currentSelection, mediaItem];
    }

    // Item is already selected — remove it.
    return currentSelection.filter(selected => selected.resourceId !== mediaItem.resourceId);
  }

  /**
   * Replaces the current selection with a contiguous range between the anchor
   * (first item in the current selection) and the clicked item.
   *
   * Consecutive shift-clicks always expand or contract relative to the same
   * anchor, supporting both forward and reverse ranges:
   *   Click 6        → selection = [6]        (anchor = 6)
   *   Shift+click 10 → selection = [6..10]    (anchor still 6)
   *   Shift+click 3  → selection = [3..6]     (anchor still 6)
   *
   * @param {MediaItem[]} currentSelection
   * @param {MediaItem} mediaItem - The shift-clicked target item
   * @param {MediaItem[]} currentPageItems - The items currently rendered on the page
   * @returns {MediaItem[]} Updated selection array
   */
  function multiSelectWithShift (currentSelection: MediaItem[], mediaItem: MediaItem, currentPageItems: MediaItem[]): MediaItem[] {
    if (!anchorItem.value || currentSelection.length === 0) {
      // Nothing is selected yet — treat the clicked item as the sole selection.
      // It will become the anchor for any subsequent shift-clicks.
      mediaSelectionStore.setAnchorItem(mediaItem);
      return [mediaItem];
    }

    // Media items displayed on the current page, used to resolve indices.
    const currentMediaItems = currentPageItems;

    // Use the stored anchor — never derived from currentSelection so it stays
    // stable across consecutive shift-range operations.
    const anchorIndex = currentMediaItems.findIndex(item => item.resourceId === anchorItem.value?.resourceId);
    const targetIndex = currentMediaItems.findIndex(item => item.resourceId === mediaItem.resourceId);

    // If either item is no longer present in the current batch (for example after
    // pagination or filter changes), fall back to a safe single selection and
    // reset the anchor to the newly clicked item.
    if (anchorIndex === -1 || targetIndex === -1) {
      mediaSelectionStore.setAnchorItem(mediaItem);
      return [mediaItem];
    }

    // Sort the two indices to support both forward and reverse ranges.
    const [start, end] = [anchorIndex, targetIndex].sort((a, b) => a - b);

    // Replace the entire selection with the new range — do not extend or merge.
    return currentMediaItems.slice(start, end + 1);
  }

  /**
   * Selects all provided items, excluding virtual placeholder cards
   * (new LED / new Videowall entries that are not real media items).
   * @param {MediaItem[]} mediaItems - Items currently visible in the list
   */
  function selectAll (mediaItems: MediaItem[]) {
    mediaSelectionStore.setSelection(mediaItems);
  }

  /**
   * Clears the current media selection.
   * Should be called when the user navigates away, applies new filters, or
   * clicks on the background outside the media cards.
   */
  function clearSelection () {
    mediaSelectionStore.clearSelection();
  }

  /**
   * Fetches full details for a media item and merges them into the selection store.
   * @param {Object|Object[]} mediaItems
   */
  async function fetchAndPopulateDetails (mediaItems: MediaItem | MediaItem[]) {
    mediaSelectionStore.setIsFetchingDetails(true);
    const items = Array.isArray(mediaItems) ? mediaItems : [mediaItems];

    const results = await Promise.allSettled(
      items.map(mediaItem => mediaStore.fetchDetails(mediaItem.resourceId))
    );

    const detailedItems = results
      .map((result) => (result.status === 'fulfilled' ? result.value.response.data : null))
      .filter(Boolean) as MediaItem[];

    mediaSelectionStore.populateSelectedItemsDetails(detailedItems);

    mediaSelectionStore.setIsFetchingDetails(false);
  }

  return {
    selection,
    hasSelection,
    selectedIds,
    isFetchingDetails,

    selectMedia,
    selectAll,
    clearSelection,
  };
}
